import {
  BriefcaseBusiness,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  CheckCheck,
  ClipboardList,
  Contact,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  PieChart,
  ReceiptText,
  Settings,
  Users,
  UsersRound,
} from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from './me';
import { useQ } from './hooks';

/**
 * Navigation model. One entry of the menu is a section; a section with several tabs shows them as a tab bar above
 * the page (same heading, one click to the neighbouring page). Visibility follows the rights of the person, so
 * nobody sees a menu entry that leads to "no access". The model is the single source for the menu, the tab bar,
 * the page title in the top bar and the shortcut tiles of the start page.
 */
export interface NavTab {
  id: string;
  to: string;
  label: string;
  show: boolean;
  badge?: number;
  /** red badge: something to correct */
  badgeTone?: 'bad';
}

export interface NavSection {
  id: string;
  label: string;
  icon: ReactNode;
  tabs: NavTab[];
}

export interface NavGroup {
  id: string;
  label: string;
  sections: NavSection[];
}

const ICON = 'size-4 shrink-0';

/** The route belongs to the tab (nested routes such as /projects/123 belong to /projects). */
export function routeMatches(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function sumBadge(tabs: NavTab[]): number {
  return tabs.filter((t) => t.show).reduce((n, t) => n + (t.badge ?? 0), 0);
}

export function useNav(): { groups: NavGroup[]; badges: { approvals: number; leave: number; rejected: number } } {
  const { t } = useTranslation();
  const { me, can, isLead } = useMe();
  const approvals = useQ('approvals.summary', {}, { refetchInterval: 120_000 });
  const badges = useQ('time.badges', {}, { refetchInterval: 120_000 });
  const approvalsTotal = approvals.data?.total ?? 0;
  const leavePending = badges.data?.leavePending ?? 0;
  const rejected = badges.data?.rejected ?? 0;
  const employee = me.person.kind === 'employee';
  const leadOrFinal = isLead() || can('time.approve.final', 'time.manage');

  const groups = useMemo<NavGroup[]>(() => {
    const single = (id: string, label: string, icon: ReactNode, to: string, show: boolean, extra: Partial<NavTab> = {}): NavSection => ({
      id,
      label,
      icon,
      tabs: [{ id, to, label, show, ...extra }],
    });
    return [
      {
        id: 'mine',
        label: t('nav.groupMine'),
        sections: [
          single('overview', t('nav.overview'), <LayoutDashboard className={ICON} />, '/', true),
          single('week', t('nav.week'), <CalendarDays className={ICON} />, '/week', true),
          single('entries', t('nav.entries'), <ClipboardList className={ICON} />, '/entries', true, { badge: rejected, badgeTone: 'bad' }),
          single('my-absences', t('nav.myAbsences'), <CalendarCheck className={ICON} />, '/my-absences', employee),
        ],
      },
      {
        id: 'projects',
        label: t('nav.groupProjects'),
        sections: [
          single('projects', t('nav.projects'), <BriefcaseBusiness className={ICON} />, '/projects', true),
          single('tasks', t('nav.tasks'), <ListChecks className={ICON} />, '/tasks', true),
          single('team', t('nav.team'), <UsersRound className={ICON} />, '/team', employee),
        ],
      },
      {
        id: 'lead',
        label: t('nav.groupLead'),
        sections: [
          {
            id: 'approvals',
            label: t('nav.approvalsGroup'),
            icon: <CheckCheck className={ICON} />,
            tabs: [
              // time entries: project leads, office, management
              { id: 'approvals', to: '/approvals', label: t('nav.subTime'), show: leadOrFinal, badge: approvalsTotal },
              // absence requests: "Personal" and the approvers set for people (e.g. team leads)
              { id: 'leave-requests', to: '/leave-requests', label: t('nav.leaveRequests'), show: me.leaveApprover, badge: leavePending },
            ],
          },
          {
            id: 'planning',
            label: t('nav.planningGroup'),
            icon: <CalendarRange className={ICON} />,
            tabs: [
              { id: 'planning', to: '/planning', label: t('nav.planning'), show: isLead() || can('workload.manage') },
              { id: 'workload', to: '/workload', label: t('nav.workload'), show: isLead() || can('workload.manage', 'kpi.team.view', 'hr.manage') },
            ],
          },
          {
            id: 'insights',
            label: t('nav.insightsGroup'),
            icon: <PieChart className={ICON} />,
            tabs: [
              { id: 'cockpit', to: '/cockpit', label: t('nav.cockpit'), show: !!me.cockpit },
              { id: 'analysis', to: '/analysis', label: t('nav.analysis'), show: !!me.analysis },
              { id: 'evaluation', to: '/evaluation', label: t('nav.evaluation'), show: can('kpi.team.view') },
            ],
          },
          single('customers', t('nav.customers'), <Contact className={ICON} />, '/customers', can('customers.manage')),
          single('billing', t('nav.billing'), <ReceiptText className={ICON} />, '/billing', can('billing.manage')),
        ],
      },
      {
        id: 'admin',
        label: t('nav.groupAdmin'),
        sections: [
          {
            id: 'people',
            label: t('nav.peopleGroup'),
            icon: <Users className={ICON} />,
            tabs: [
              { id: 'people', to: '/people', label: t('nav.people'), show: can('hr.manage', 'users.manage') },
              { id: 'absences', to: '/absences', label: t('nav.absences'), show: can('hr.manage', 'settings.manage') },
            ],
          },
          single('announcements', t('nav.announcements'), <Megaphone className={ICON} />, '/announcements', can('announcements.manage')),
          single('admin', t('nav.admin'), <Settings className={ICON} />, '/admin', can('settings.manage', 'period.manage', 'sync.manage', 'backup.manage', 'audit.view', 'data.export.all', 'projects.manage')),
        ],
      },
    ]
      .map((g) => ({ ...g, sections: g.sections.filter((s) => s.tabs.some((x) => x.show)) }))
      .filter((g) => g.sections.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, me, approvalsTotal, leavePending, rejected, leadOrFinal]);

  return { groups, badges: { approvals: approvalsTotal, leave: leavePending, rejected } };
}

/** Section and tab that belong to a route (null for pages outside the menu, e.g. profile and help). */
export function locate(groups: NavGroup[], pathname: string): { section: NavSection; tab: NavTab } | null {
  let best: { section: NavSection; tab: NavTab } | null = null;
  for (const g of groups) {
    for (const s of g.sections) {
      for (const tab of s.tabs) {
        if (!tab.show || !routeMatches(tab.to, pathname)) continue;
        // the longest route wins ("/" only for the start page)
        if (!best || tab.to.length > best.tab.to.length) best = { section: s, tab };
      }
    }
  }
  return best;
}

export function visibleTabs(section: NavSection): NavTab[] {
  return section.tabs.filter((x) => x.show);
}

/** The tab for a route if the person may see it (shortcut tiles of the start page use the same rights as the menu). */
export function findTab(groups: NavGroup[], to: string): NavTab | null {
  for (const g of groups) for (const s of g.sections) for (const tab of s.tabs) if (tab.show && tab.to === to) return tab;
  return null;
}
